import type { MetaDescriptor } from "@remix-run/node";

export function truncateForMeta(text: string | undefined | null, maxLength = 160): string {
  if (!text) return "";
  // Strip HTML tags
  const clean = text
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (clean.length <= maxLength) return clean;
  return clean.slice(0, maxLength - 3) + "...";
}

type ParentMatchData = { companyInfo?: { data?: { companyShortName?: string } } } | undefined;

export function getCompanyFromMatches(matches: { id: string; data: unknown }[]): string {
  const parentData = matches.find((m) => m.id === "routes/modules/layout")?.data as ParentMatchData;
  return parentData?.companyInfo?.data?.companyShortName || "AcademIA";
}

/**
 * Origen del sitio POR ENTORNO (dev/staging/prod cada uno buildea el suyo). Los
 * crawlers de WhatsApp/Facebook/X exigen URLs ABSOLUTAS en og:image y og:url: con una
 * ruta relativa no muestran preview, y con una absoluta hardcodeada a prod se rompe en
 * dev (pide la imagen a prod, donde no existe, y recibe HTML).
 */
const SITE_URL = (import.meta.env?.VITE_APP_URL || "").replace(/\/+$/, "");
const DEFAULT_OG_IMAGE = "/og-image-v2.png";

export function absoluteUrl(path: string): string {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

export function ogImageUrl(imageUrl: string | undefined | null): string {
  if (!imageUrl) return DEFAULT_OG_IMAGE;
  return `/og-proxy?url=${encodeURIComponent(imageUrl)}`;
}

/**
 * Build a standardized set of 10 meta tags for every route.
 * Keeping the count and keys consistent prevents React from inserting/removing
 * <head> children across navigations, which avoids hydration crashes.
 */
export function buildMeta(opts: {
  title: string;
  description?: string;
  image?: string;
  type?: string;
  path?: string;
}): MetaDescriptor[] {
  const image = absoluteUrl(opts.image || DEFAULT_OG_IMAGE);
  const description = opts.description || "";
  // Nota: width/height describen la OG por defecto (1200x630). Las imágenes de curso
  // pasan por /og-proxy con otra relación; se declaran igual para que el set de tags
  // sea SIEMPRE el mismo entre rutas (contar distinto rompía la hidratación del <head>).
  return [
    { title: opts.title },
    { name: "description", content: description },
    { property: "og:site_name", content: "AcademIA" },
    { property: "og:type", content: opts.type || "website" },
    { property: "og:url", content: absoluteUrl(opts.path || "/") },
    { property: "og:title", content: opts.title },
    { property: "og:description", content: description },
    { property: "og:image", content: image },
    { property: "og:image:secure_url", content: image },
    { property: "og:image:type", content: "image/png" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: opts.title },
    { property: "og:locale", content: "es_AR" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: opts.title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: image },
  ];
}
